import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, TrendingDown, Calendar, Scale, Activity, Mic, CheckCircle2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { publicAPI } from '../api';

const PublicClientView = () => {
  const { accessToken } = useParams();
  const [clientData, setClientData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadClientData();
  }, [accessToken]);

  const loadClientData = async () => {
    try {
      const [clientRes, progressRes] = await Promise.all([
        publicAPI.getClient(accessToken),
        publicAPI.getProgress(accessToken),
      ]);
      setClientData(clientRes.data);
      setProgress(progressRes.data);
    } catch (err) {
      setError('No se pudo cargar la información del cliente');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-stone-600">{error}</p>
        </div>
      </div>
    );
  }

  const current = progress?.current_stats || {};

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E8E6E1]">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#6B8E7D] to-[#8AA894] flex items-center justify-center text-white font-semibold text-2xl">
              {clientData?.name?.charAt(0)}
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {clientData?.name}
              </h1>
              <p className="text-stone-500">
                Coach: {clientData?.coach_name}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="card-organic">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 rounded-xl bg-[#6B8E7D]/10">
                <Scale size={20} className="text-[#6B8E7D]" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Peso actual</span>
            </div>
            <p className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {current.weight_kg || '—'} <span className="text-lg text-stone-400">kg</span>
            </p>
          </div>

          <div className="card-organic">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 rounded-xl bg-[#C25934]/10">
                <Activity size={20} className="text-[#C25934]" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Grasa corporal</span>
            </div>
            <p className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {current.body_fat_avg?.toFixed(1) || '—'} <span className="text-lg text-stone-400">%</span>
            </p>
          </div>

          <div className="card-organic">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 rounded-xl bg-stone-100">
                <Calendar size={20} className="text-stone-600" />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wider text-stone-500">Mediciones</span>
            </div>
            <p className="text-3xl font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {progress?.measurements_count || 0}
            </p>
          </div>
        </div>

        {/* Weight Chart */}
        {progress?.weight_trend?.length > 1 && (
          <div className="card-organic mb-6">
            <h3 className="text-lg font-medium text-stone-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tu progreso de peso
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={progress.weight_trend}>
                  <defs>
                    <linearGradient id="weightGradientPublic" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6B8E7D" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#6B8E7D" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="date" tick={{ fill: '#57605B', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#57605B', fontSize: 12 }} domain={['auto', 'auto']} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E8E6E1',
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#6B8E7D" 
                    strokeWidth={2}
                    fill="url(#weightGradientPublic)" 
                    name="Peso (kg)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Body Fat Chart */}
        {progress?.body_fat_trend?.length > 1 && (
          <div className="card-organic mb-6">
            <h3 className="text-lg font-medium text-stone-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Evolución de grasa corporal
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={progress.body_fat_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="date" tick={{ fill: '#57605B', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#57605B', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #E8E6E1',
                      borderRadius: '12px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="value" 
                    stroke="#C25934" 
                    strokeWidth={2} 
                    name="Grasa corporal (%)" 
                    dot={{ fill: '#C25934' }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Latest Consultation Summary */}
        {progress?.last_consultation_summary && (
          <div className="card-organic">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-[#C25934]/10">
                <Mic size={20} className="text-[#C25934]" />
              </div>
              <h3 className="text-lg font-medium text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Resumen de última consulta
              </h3>
            </div>
            <p className="text-stone-600 leading-relaxed">
              {progress.last_consultation_summary}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 py-8 border-t border-stone-200">
          <p className="text-stone-400 text-sm">
            Powered by <span className="font-medium text-[#6B8E7D]">Antropia</span>
          </p>
        </div>
      </main>
    </div>
  );
};

export default PublicClientView;
